import React, { useEffect, useMemo, useState } from "react";
import { fetchAllSnapshots } from "@/lib/api";
import { Info, ArrowsLeftRight, Calendar } from "@phosphor-icons/react";
import DualExplainer from "@/components/DualExplainer";

const INDICES = [
  { key: "ndvi", label: "NDVI", color: "#00FA9A" },
  { key: "ndmi", label: "NDMI", color: "#4DA3FF" },
  { key: "chlorophyll_a", label: "Chlorophyll-a", color: "#FFC24B" },
  { key: "turbidity", label: "Turbidität", color: "#FF5500" },
];

// Approximate mapping: lon 7..14, lat 47..55 → SVG 0..800 / 0..500
function project(lon, lat) {
  const x = ((lon - 7) / (14 - 7)) * 800;
  const y = ((55 - lat) / (55 - 47)) * 500;
  return [x, y];
}

/* ------------------------------------------------------------------------
 * NDVI-Farbskala — rot → gelb → grün, intuitiv lesbar
 * Klassen mit Layen-Übersetzung. Die Klassen-Ranges sind speziell für NDVI
 * abgestimmt; für andere Indizes nehmen wir eine leicht abgewandelte Skala.
 * ------------------------------------------------------------------------ */
const NDVI_BANDS = [
  { from: -1, to: 0.1, color: "#7A2A1B", label: "Bare", layman: "Boden / kein Bewuchs" },
  { from: 0.1, to: 0.3, color: "#FF5500", label: "Stress", layman: "Stark gestresst — Hilfe nötig" },
  { from: 0.3, to: 0.5, color: "#FFC24B", label: "Unter-Soll", layman: "Mäßig — beobachten" },
  { from: 0.5, to: 0.7, color: "#9BB23C", label: "OK", layman: "Solide Vegetation" },
  { from: 0.7, to: 1.0, color: "#00FA9A", label: "Vital", layman: "Sehr gesund · sattgrün" },
];
const GENERIC_BANDS = [
  { from: 0.0, to: 0.2, color: "#7A2A1B", label: "Sehr niedrig", layman: "Wenig vorhanden" },
  { from: 0.2, to: 0.4, color: "#FF5500", label: "Niedrig", layman: "Unter Mittelwert" },
  { from: 0.4, to: 0.6, color: "#FFC24B", label: "Mittel", layman: "Normal" },
  { from: 0.6, to: 0.8, color: "#9BB23C", label: "Erhöht", layman: "Über Mittelwert" },
  { from: 0.8, to: 1.0, color: "#00FA9A", label: "Sehr hoch", layman: "Auffällig hoch" },
];

function bandsFor(indexKey) {
  return indexKey === "ndvi" ? NDVI_BANDS : GENERIC_BANDS;
}

function bandFor(value, bands) {
  if (value === null || value === undefined || Number.isNaN(value)) return null;
  for (const b of bands) {
    if (value >= b.from && value < b.to) return b;
  }
  return bands[bands.length - 1];
}

function colorForValue(value, bands) {
  const b = bandFor(value, bands);
  return b?.color || "rgba(255,255,255,0.10)";
}

/* Average a slice of the recent NDVI series for a given window. */
function avgWindow(values, window) {
  if (!values || values.length === 0) return null;
  const slice = values.slice(-window).filter((v) => v !== null && v !== undefined);
  if (!slice.length) return null;
  return slice.reduce((a, b) => a + b, 0) / slice.length;
}

export default function Satellite() {
  const [data, setData] = useState(null);
  const [indexKey, setIndexKey] = useState("ndvi");
  const [mode, setMode] = useState("single"); // single | compare
  const [dayIdx, setDayIdx] = useState(29);

  useEffect(() => {
    (async () => {
      const d = await fetchAllSnapshots();
      setData(d);
    })();
  }, []);

  const snaps = data?.snapshots || [];
  const regions = data?.regions || [];
  const bands = bandsFor(indexKey);

  const daysAvailable = snaps[0]?.satellite?.dates?.length || 30;
  const dayLabel = snaps[0]?.satellite?.dates?.[Math.min(dayIdx, daysAvailable - 1)] || "–";

  const currentIndex = INDICES.find((i) => i.key === indexKey);

  return (
    <div className="space-y-6" data-testid="satellite-root">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="eid-label">Copernicus Sentinel-2/3</div>
          <h1 className="text-3xl md:text-4xl font-medium tracking-tight mt-1">Satelliten-Ansicht</h1>
          <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
            Multispektrale Indizes für Mitteleuropa. Wähle Index und Zeitfenster.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <div className="flex gap-1 p-1 rounded-sm bg-white/5 border border-white/10" data-testid="mode-selector">
            <button
              onClick={() => setMode("single")}
              data-testid="mode-btn-single"
              className={`px-3 py-1.5 text-xs rounded-sm flex items-center gap-1 ${
                mode === "single" ? "bg-[#F5F5F5] text-[#0A0A0A]" : "text-zinc-400 hover:text-white"
              }`}
            >
              <Calendar size={12} /> Einzeltag
            </button>
            <button
              onClick={() => setMode("compare")}
              data-testid="mode-btn-compare"
              className={`px-3 py-1.5 text-xs rounded-sm flex items-center gap-1 ${
                mode === "compare" ? "bg-[#F5F5F5] text-[#0A0A0A]" : "text-zinc-400 hover:text-white"
              }`}
            >
              <ArrowsLeftRight size={12} /> 7T vs. 30T
            </button>
          </div>
          <div className="flex gap-2" data-testid="index-selector">
            {INDICES.map((i) => (
              <button
                key={i.key}
                data-testid={`index-btn-${i.key}`}
                onClick={() => setIndexKey(i.key)}
                className={`px-3 py-2 text-sm rounded-sm border transition-colors ${
                  indexKey === i.key
                    ? "bg-[#F5F5F5] text-[#0A0A0A] border-white/15"
                    : "bg-[#121212] text-white border-white/10 hover:bg-white/5"
                }`}
              >
                {i.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      <DualExplainer
        title="Was zeigen diese Satellitenbilder?"
        testid="satellite-explainer"
        simple={
          <>
            <p>
              Ein Satellit fotografiert alle 3–5 Tage jedes Feld in Mitteleuropa — aus
              700 km Höhe, mit 10 m Auflösung. Aus den Fotos werden vier Messwerte berechnet:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>NDVI</strong> — wie „grün" und dicht der Bestand ist. <strong className="text-[#00FA9A]">Grün = vital</strong>, <strong className="text-[#FF7A45]">Rot = gestresst</strong>.</li>
              <li><strong>NDMI</strong> — wie viel Wasser in den Blättern steckt. Hoch = gut versorgt.</li>
              <li><strong>Chlorophyll</strong> — wie stark die Pflanze aktuell Photosynthese betreibt.</li>
              <li><strong>Turbidität</strong> — wie trüb Gewässer in der Nähe sind (Erosion-Indikator).</li>
            </ul>
            <p className="text-[#00FA9A]">
              <strong>Praxis-Tipp:</strong> Mit „<strong>7T vs. 30T</strong>"-Modus siehst du die letzte
              Woche neben dem Monatsmittel. Plötzlicher Farbsprung von Grün → Gelb/Rot
              bedeutet: rausfahren und schauen.
            </p>
          </>
        }
        science={
          <>
            <p>
              <strong>Quelle:</strong> Copernicus Sentinel-2 (MSI, 10/20 m, Re-Visit 3–5 Tage)
              + Sentinel-3 (OLCI, 300 m) für Gewässer-Indizes. Cloud-Masking via QA60 Band.
            </p>
            <p>
              <strong>Formeln:</strong><br/>
              <code className="font-mono text-[11px]">NDVI = (B8 − B4) / (B8 + B4)</code><br/>
              <code className="font-mono text-[11px]">NDMI = (B8 − B11) / (B8 + B11)</code><br/>
              <code className="font-mono text-[11px]">Chl_a ≈ B5 / B4 × k</code> (Gitelson-2003 Proxy)
            </p>
            <p>
              <strong>Heatmap-Projektion:</strong> Lineare Interpolation in Region-Zentroide
              (nicht rigoros). 7-Tage-Mittel = letzte 7 Snapshot-Tage; 30-Tage-Mittel = volle
              Serie. Für wissenschaftliche Auswertung: Masken auf Feld-Grenzen
              (SCL-Layer / LPIS-Parzellen), Zonal-Statistik per GeoPandas/rasterstats,
              Baseline-Vergleich gegen gleitenden 5-Jahres-Mittelwert.
            </p>
          </>
        }
      />

      <section className={`grid gap-3 ${mode === "compare" ? "grid-cols-1" : "grid-cols-1 lg:grid-cols-3"}`}>
        {mode === "single" && (
          <div className="eid-card p-4 lg:col-span-2 relative" data-testid="heatmap-single">
            <div className="flex items-baseline justify-between">
              <h2 className="text-xl font-medium">{currentIndex?.label} · Heatmap</h2>
              <div className="eid-label">Datum · <span className="text-white">{dayLabel}</span></div>
            </div>
            <Heatmap
              regions={regions}
              snaps={snaps}
              indexKey={indexKey}
              bands={bands}
              valueFor={(values) => values?.[Math.min(dayIdx, (values?.length || 1) - 1)]}
              testid="heat-rect"
            />
            <div className="mt-4">
              <div className="flex justify-between items-center">
                <label className="eid-label">Zeit-Slider · Tage zurück</label>
                <span className="font-mono text-xs text-zinc-400">
                  {daysAvailable - 1 - dayIdx} d · {dayLabel}
                </span>
              </div>
              <input
                type="range"
                min="0"
                max={Math.max(0, daysAvailable - 1)}
                value={dayIdx}
                onChange={(e) => setDayIdx(parseInt(e.target.value, 10))}
                data-testid="day-slider"
                className="w-full accent-[#00FA9A] mt-2"
              />
            </div>
          </div>
        )}

        {mode === "compare" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="heatmap-compare">
            <CompareCard
              title="Letzte 7 Tage"
              hint="Zustand jetzt"
              regions={regions}
              snaps={snaps}
              indexKey={indexKey}
              bands={bands}
              window={7}
              testid="compare-7d"
            />
            <CompareCard
              title="Letzte 30 Tage"
              hint="Monats-Mittel"
              regions={regions}
              snaps={snaps}
              indexKey={indexKey}
              bands={bands}
              window={30}
              testid="compare-30d"
            />
            <DeltaTable
              regions={regions}
              snaps={snaps}
              indexKey={indexKey}
              bands={bands}
            />
          </div>
        )}

        {mode === "single" && (
          <Legend
            bands={bands}
            indexKey={indexKey}
            currentLabel={currentIndex?.label}
            source={snaps[0]?.satellite?.source}
            regions={regions}
          />
        )}
      </section>
    </div>
  );
}

function Heatmap({ regions, snaps, indexKey, bands, valueFor, testid }) {
  return (
    <div className="mt-4 aspect-[8/5] crosshair-bg border border-white/10 relative overflow-hidden">
      <svg viewBox="0 0 800 500" className="w-full h-full">
        <rect x="0" y="0" width="800" height="500" fill="#121212" />
        {[47, 49, 51, 53, 55].map((lat) => {
          const [, y] = project(7, lat);
          return (
            <g key={lat}>
              <line x1="0" x2="800" y1={y} y2={y} stroke="rgba(255,255,255,0.08)" strokeDasharray="3 4" />
              <text x="4" y={y - 4} fontSize="10" fill="#8A8A93" fontFamily="JetBrains Mono">
                {lat}°N
              </text>
            </g>
          );
        })}
        {[7, 9, 11, 13].map((lon) => {
          const [x] = project(lon, 47);
          return (
            <g key={lon}>
              <line x1={x} x2={x} y1="0" y2="500" stroke="rgba(255,255,255,0.08)" strokeDasharray="3 4" />
              <text x={x + 4} y="12" fontSize="10" fill="#8A8A93" fontFamily="JetBrains Mono">
                {lon}°E
              </text>
            </g>
          );
        })}
        {regions.map((r) => {
          const s = snaps.find((x) => x.region_id === r.id);
          const values = s?.satellite?.[indexKey];
          const v = valueFor(values);
          const [minLon, minLat, maxLon, maxLat] = r.bbox;
          const [x1, y1] = project(minLon, maxLat);
          const [x2, y2] = project(maxLon, minLat);
          const w = x2 - x1;
          const h = y2 - y1;
          const fill = colorForValue(v, bands);
          const band = bandFor(v, bands);
          return (
            <g key={r.id} data-testid={`${testid}-${r.id}`}>
              <rect x={x1} y={y1} width={w} height={h} fill={fill} opacity="0.88" stroke="#FFFFFF" strokeWidth="1">
                <title>{`${r.public_label} — ${v?.toFixed?.(2) ?? "–"} · ${band?.layman || "keine Daten"}`}</title>
              </rect>
              <rect x={x1} y={y1 - 18} width={Math.max(120, w)} height={16} fill="#FFFFFF" />
              <text x={x1 + 6} y={y1 - 6} fontSize="10" fill="white" fontFamily="JetBrains Mono">
                {r.public_label} · {v?.toFixed?.(2) ?? "–"}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function CompareCard({ title, hint, regions, snaps, indexKey, bands, window, testid }) {
  return (
    <div className="eid-card p-3" data-testid={testid}>
      <div className="flex items-baseline justify-between">
        <h3 className="text-base font-medium">{title}</h3>
        <span className="eid-label">{hint}</span>
      </div>
      <Heatmap
        regions={regions}
        snaps={snaps}
        indexKey={indexKey}
        bands={bands}
        valueFor={(values) => avgWindow(values, window)}
        testid={`${testid}-rect`}
      />
      <BandStripe bands={bands} />
    </div>
  );
}

function DeltaTable({ regions, snaps, indexKey, bands }) {
  const rows = regions
    .map((r) => {
      const s = snaps.find((x) => x.region_id === r.id);
      const values = s?.satellite?.[indexKey];
      const v7 = avgWindow(values, 7);
      const v30 = avgWindow(values, 30);
      const delta = v7 != null && v30 != null ? v7 - v30 : null;
      return { r, v7, v30, delta };
    })
    .filter((row) => row.delta !== null)
    .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));

  return (
    <div className="eid-card p-4 lg:col-span-2" data-testid="compare-delta-table">
      <h3 className="text-base font-medium">Veränderung 7T vs 30T</h3>
      <p className="text-xs text-zinc-400 mt-0.5">
        Sortiert nach Stärke der Bewegung. Negativer Wert = es geht bergab, positiver = Erholung.
      </p>
      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">
            <tr>
              <th className="text-left py-2">Region</th>
              <th className="text-right py-2">7T</th>
              <th className="text-right py-2">30T</th>
              <th className="text-right py-2">Δ</th>
              <th className="text-left pl-3 py-2">Klartext</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ r, v7, v30, delta }) => {
              const band7 = bandFor(v7, bands);
              const trend = delta > 0.04 ? "↑ steigt" : delta < -0.04 ? "↓ fällt" : "→ stabil";
              const trendColor = delta > 0.04 ? "#00FA9A" : delta < -0.04 ? "#FF5500" : "#A1A1AA";
              return (
                <tr key={r.id} className="border-t border-white/10" data-testid={`delta-row-${r.id}`}>
                  <td className="py-1.5">{r.public_label}</td>
                  <td className="text-right font-mono">
                    <span
                      className="inline-block w-2 h-2 rounded-full mr-1"
                      style={{ background: band7?.color || "rgba(255,255,255,0.10)" }}
                    />
                    {v7?.toFixed?.(2)}
                  </td>
                  <td className="text-right font-mono text-zinc-400">{v30?.toFixed?.(2)}</td>
                  <td className="text-right font-mono font-medium" style={{ color: trendColor }}>
                    {delta > 0 ? "+" : ""}{delta.toFixed(2)}
                  </td>
                  <td className="pl-3 text-xs" style={{ color: trendColor }}>{trend}</td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={5} className="py-3 text-xs text-zinc-500 text-center">
                  Noch keine ausreichenden Daten für 7T/30T-Vergleich.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BandStripe({ bands }) {
  return (
    <div className="mt-3" data-testid="band-stripe">
      <div className="h-3 flex rounded-sm overflow-hidden">
        {bands.map((b) => (
          <div
            key={b.label}
            style={{ background: b.color, width: `${100 / bands.length}%` }}
            title={`${b.label} · ${b.layman}`}
          />
        ))}
      </div>
      <div className="flex text-[9px] font-mono uppercase tracking-wider text-zinc-400 mt-1">
        {bands.map((b) => (
          <div key={b.label} style={{ width: `${100 / bands.length}%` }} className="text-center">
            {b.label}
          </div>
        ))}
      </div>
    </div>
  );
}

function Legend({ bands, indexKey, currentLabel, source, regions }) {
  return (
    <aside className="eid-card p-4" data-testid="satellite-legend">
      <h3 className="text-lg font-medium">Legende — {currentLabel}</h3>
      <p className="text-xs text-zinc-400 mt-1">
        {indexKey === "ndvi"
          ? "NDVI misst die Vitalität des Bestands. Rot = Stress, Grün = vital."
          : "Klassen sind je Index angepasst. Grün = positiver/erhöhter Wert."}
      </p>

      <div className="mt-3 space-y-1.5" data-testid="legend-bands">
        {bands.map((b) => (
          <div key={b.label} className="flex items-center gap-2 text-xs">
            <span
              className="inline-block w-4 h-4 rounded-sm shrink-0"
              style={{ background: b.color }}
            />
            <div className="flex-1">
              <div className="font-medium">{b.label}</div>
              <div className="text-[10px] text-zinc-500 leading-snug">{b.layman}</div>
            </div>
            <span className="font-mono text-[10px] text-zinc-500">
              {b.from.toFixed(1)}–{b.to.toFixed(1)}
            </span>
          </div>
        ))}
      </div>

      <div className="eid-divider my-3" />
      <div className="text-xs text-zinc-400 flex gap-2 items-start">
        <Info size={14} className="mt-0.5 shrink-0 text-zinc-500" />
        <p>
          Die Klassen sind einfache Daumenwerte. Praktiker:innen ergänzen sie mit
          Bodenproben, Wettermodellen und ein paar Schritten ins Feld.
        </p>
      </div>
      <div className="eid-divider my-3" />
      <div>
        <div className="eid-label">Quelle</div>
        <div className="font-mono text-sm">{source || "–"}</div>
        <div className="eid-label mt-2">Regionen ({regions.length})</div>
        <div className="max-h-40 overflow-y-auto pr-1">
          {regions.slice(0, 30).map((r) => (
            <div key={r.id} className="flex justify-between text-xs mt-1">
              <span>{r.public_label}</span>
              <span className="text-zinc-500 font-mono">{r.id}</span>
            </div>
          ))}
        </div>
      </div>
    </aside>
  );
}
